import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { api } from '../../lib/api';
import { euro } from '../../lib/format';
import { useToasts } from '../../lib/toast';
import type { ZClosureRow, ZDetail, ZPreview } from '@taj/types';

const PAY_LABEL: Record<string, string> = { CASH: 'Espèces', CARD: 'CB & Carte ticket', CARTE_TICKET: 'CB & Carte ticket', TICKET: 'Ticket papier', ONLINE: 'En ligne' };
const TYPE_LABEL: Record<string, string> = { DINE_IN: 'Sur place', TAKEAWAY: 'À emporter', DELIVERY: 'Livraison', CLICK_COLLECT: 'Click & Collect' };
const STATUS_LABEL: Record<string, string> = { PENDING_PAYMENT: 'en attente de paiement', RECEIVED: 'reçue', PREPARING: 'en préparation', READY: 'prête' };
const esc = (s: string) => (s ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string));

export function ClotureManager({ onBack }: { onBack: () => void }) {
  const push = useToasts((s) => s.push);
  const [preview, setPreview] = useState<ZPreview | null>(null);
  const [list, setList] = useState<ZClosureRow[]>([]);
  const [closing, setClosing] = useState(false);
  // Ouverture de service : l'employé se ré-identifie (son nom part sur le Ticket Z).
  const [askOpen, setAskOpen] = useState(false);
  const [opening, setOpening] = useState(false);
  const [mode, setMode] = useState<'pin' | 'admin'>('pin');
  const [pin, setPin] = useState('');
  const [name, setName] = useState('');
  const [pwd, setPwd] = useState('');
  const [float, setFloat] = useState(''); // fond de caisse (monnaie de départ)

  const load = () => {
    api.cloturePreview().then(setPreview).catch((e) => push(e.message, 'warn'));
    api.clotureList().then(setList).catch((e) => push(e.message, 'warn'));
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  /** Aperçu / PDF : ouvre le Ticket Z complet dans une fenêtre d'impression du navigateur. */
  const printZ = (z: ZClosureRow) => {
    const d = new Date(z.createdAt).toLocaleString('fr-FR');
    const det: Partial<ZDetail> = z.detail ?? {};
    const rows = (entries: [string, number][]) =>
      entries.map(([k, v]) => `<tr><td>${esc(PAY_LABEL[k] ?? TYPE_LABEL[k] ?? k)}</td><td class="r">${euro(v)}</td></tr>`).join('') || '<tr><td colspan="2">—</td></tr>';

    const sales = (det.byCategory ?? []).map((c) => `
      <tr class="b"><td>${esc(c.category)}</td><td class="r">${c.qty} art. · ${euro(c.revenueCents)}</td></tr>
      ${c.products.map((p) => `<tr><td>&nbsp;&nbsp;${p.qty}× ${esc(p.name)}</td><td class="r">${euro(p.revenueCents)}</td></tr>`).join('')}
    `).join('') || '<tr><td colspan="2">Aucune vente.</td></tr>';

    const cancelled = (det.cancelled ?? []).map((c) =>
      `<tr><td>${esc(c.token ?? '#' + c.number)} · ${esc(TYPE_LABEL[c.type] ?? c.type)}${c.clientName ? ' · ' + esc(c.clientName) : ''}${c.by ? ' · ' + esc(c.by) : ''}</td><td class="r">${euro(c.totalCents)}</td></tr>`).join('') || '<tr><td colspan="2">Aucune.</td></tr>';

    const voids = (det.voids ?? []).map((v) =>
      `<tr><td>${esc(v.orderToken ?? '#' + v.orderNumber)} · ${esc(PAY_LABEL[v.method] ?? v.method)}${v.by ? ' · ' + esc(v.by) : ''}</td><td class="r">${euro(v.amountCents)}</td></tr>`).join('') || '<tr><td colspan="2">Aucun.</td></tr>';

    const stock = (det.stock ?? []).map((s) =>
      `<tr><td>${esc(s.name)} <span class="dim">${esc(s.category)}</span></td><td class="r">${s.quantity}${s.low ? ' (bas)' : ''}</td></tr>`).join('') || '<tr><td colspan="2">Aucun produit suivi.</td></tr>';

    const html = `<!doctype html><html><head><meta charset="utf-8"><title>Ticket Z n°${z.number}</title>
      <style>body{font-family:'Courier New',monospace;color:#000;max-width:340px;margin:auto;padding:14px;font-size:13px}
      h1{font-size:18px;text-align:center;margin:0}.c{text-align:center}.r{text-align:right;white-space:nowrap}
      table{width:100%;border-collapse:collapse;margin:4px 0}td{padding:2px 0;vertical-align:top}
      .line{border-top:1px dashed #000;margin:8px 0}.big{font-size:16px;font-weight:bold}
      .b td{font-weight:bold;padding-top:5px}.h{font-weight:bold;margin-top:4px}.dim{color:#777;font-size:11px}
      @media print{.noprint{display:none}}</style></head><body>
      <h1>TICKET Z</h1>
      <div class="c">CLÔTURE N°${z.number}</div>
      <div class="c">TAJ FUSION</div>
      <div class="line"></div>
      <div>Édité le : ${esc(d)}</div>
      <div>Séquence NF525 : ${z.fiscalSeq}</div>
      <div>Commandes : ${z.orderCount}</div>
      ${z.openedByName ? `<div>Ouverte par : ${esc(z.openedByName)}${z.openedAt ? ' — ' + new Date(z.openedAt).toLocaleString('fr-FR') : ''}</div>` : ''}
      <div>Clôturée par : ${esc(z.closedByName ?? 'Inconnu')}</div>
      <div class="line"></div>
      <table><tr><td>Total HT</td><td class="r">${euro(z.htCents)}</td></tr>
      <tr><td>TVA</td><td class="r">${euro(z.taxCents)}</td></tr>
      <tr class="big"><td>TOTAL TTC</td><td class="r">${euro(z.revenueCents)}</td></tr></table>
      <div class="line"></div>
      ${det.avgBasketCents !== undefined ? `<table><tr><td>Panier moyen</td><td class="r">${euro(det.avgBasketCents)}</td></tr><tr><td>Articles vendus</td><td class="r">${det.itemsSold ?? 0}</td></tr></table><div class="line"></div>` : ''}
      ${det.openingFloatCents !== undefined ? `<div class="h">ESPÈCES (TIROIR)</div><table><tr><td>Fond de caisse</td><td class="r">${euro(det.openingFloatCents)}</td></tr><tr><td>Espèces encaissées</td><td class="r">${euro(det.cashCollectedCents ?? 0)}</td></tr><tr class="b"><td>Attendu en tiroir</td><td class="r">${euro(det.cashExpectedCents ?? 0)}</td></tr></table><div class="line"></div>` : ''}
      <div class="h">PAR EMPLOYÉ</div>
      <table>${(det.byStaff ?? []).map((s) => `<tr class="b"><td>${esc(s.name)}</td><td class="r">${euro(s.collectedCents)} · ${s.paymentCount}</td></tr>${s.voidCount ? `<tr><td>&nbsp;&nbsp;${s.voidCount} annulation(s)</td><td class="r">−${euro(s.voidedCents)}</td></tr>` : ''}`).join('') || '<tr><td colspan="2">Aucun encaissement.</td></tr>'}</table>
      <div class="line"></div>
      <div class="h">ENCAISSEMENTS</div>
      <table>${rows(Object.entries(z.byMethod) as [string, number][])}</table>
      <div class="line"></div>
      <div class="h">PAR TYPE DE COMMANDE</div>
      <table>${rows(Object.entries(z.byType) as [string, number][])}</table>
      <div class="line"></div>
      <div class="h">PRODUITS VENDUS</div>
      <table>${sales}</table>
      <div class="line"></div>
      <div class="h">COMMANDES ANNULÉES (${(det.cancelled ?? []).length})</div>
      <table>${cancelled}</table>
      <div class="line"></div>
      <div class="h">PAIEMENTS ANNULÉS (${(det.voids ?? []).length})</div>
      <table>${voids}</table>
      <div class="line"></div>
      <div class="h">STOCK RESTANT</div>
      <table>${stock}</table>
      <div class="line"></div>
      <div class="c">— Document fiscal NF525 —</div>
      <div class="c dim">Conforme NF525 — attestation éditeur ecommx solutions</div>
      <script>window.onload=function(){window.print()}</script></body></html>`;
    const w = window.open('', '_blank', 'width=460,height=760');
    if (!w) { push('Autorise les pop-up pour imprimer', 'warn'); return; }
    w.document.write(html); w.document.close();
  };

  /** Impression sur l'imprimante thermique du poste Caisse. */
  const printThermal = async (z: ZClosureRow) => {
    try {
      const r = await api.cloturePrint(z.id);
      if (r.ok) push(`🖨️ Ticket Z n°${z.number} imprimé (${r.printer ?? 'Caisse'})`);
      else push(r.reason ?? 'Imprimante Caisse non configurée', 'warn');
    } catch (e: any) { push(e.message, 'warn'); }
  };

  const doClose = async () => {
    if (!confirm('Clôturer la caisse maintenant ?\n\nCela génère le Ticket Z (écriture fiscale NF525), fige les totaux du service et remet les numéros de commande à 101.')) return;
    setClosing(true);
    try {
      const z = await api.clotureClose();
      push(`✅ Caisse clôturée par ${z.closedByName ?? '—'} · Ticket Z n°${z.number}`);
      load();
      printZ(z); // le ticket thermique part automatiquement côté serveur
    } catch (e: any) { push(e.message, 'warn'); }
    finally { setClosing(false); }
  };

  /** Ouvre le service après identification (le nom saisi ici sera inscrit sur le Ticket Z). */
  const doOpen = async () => {
    setOpening(true);
    try {
      const openingFloatCents = Math.round((parseFloat(float.replace(',', '.')) || 0) * 100);
      const creds = mode === 'pin' ? { pin, openingFloatCents } : { fullName: name, password: pwd, openingFloatCents };
      const s = await api.clotureOpen(creds);
      push(`🔓 Service ouvert par ${s.openedByName} · fond ${euro(s.openingFloatCents)}`);
      setAskOpen(false); setPin(''); setPwd(''); setName(''); setFloat('');
      load();
    } catch (e: any) { push(e.message ?? 'Identifiants refusés', 'warn'); }
    finally { setOpening(false); }
  };

  const row = (label: string, value: string, big = false) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontWeight: big ? 900 : 600, fontSize: big ? '1.1rem' : '.95rem' }}>
      <span>{label}</span><span style={{ color: big ? 'var(--gold)' : 'var(--ink)' }}>{value}</span>
    </div>
  );

  const section = (title: string, body: ReactNode) => (
    <div>
      <div style={{ borderTop: '1px solid var(--line)', margin: '10px 0 6px' }} />
      <div style={{ fontSize: '.8rem', color: 'var(--ink-faint)', fontWeight: 800, marginBottom: 4 }}>{title}</div>
      {body}
    </div>
  );

  const empty = (txt: string) => <div style={{ fontSize: '.85rem', color: 'var(--ink-faint)' }}>{txt}</div>;
  const det = preview?.detail;
  const blocked = !!preview?.openOrders.length; // commandes ouvertes → clôture interdite

  return (
    <div className="staff-scroll">
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 0 14px' }}>
        <button className="km-back" onClick={onBack}>←</button>
        <div className="staff-title" style={{ fontSize: '1.4rem', flex: 1 }}>🧾 Clôture de caisse (Ticket Z)</div>
      </div>

      {/* Ouverture de caisse : qui a pris le service */}
      <div className="cc" style={{ maxWidth: 620, marginBottom: 16 }}>
        {preview?.session ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: '1.4rem' }}>🔓</span>
            <div style={{ flex: 1, minWidth: 180 }}>
              <div style={{ fontWeight: 800 }}>Caisse ouverte par {preview.session.openedByName}</div>
              <div style={{ fontSize: '.8rem', color: 'var(--ink-faint)' }}>
                depuis le {new Date(preview.session.openedAt).toLocaleString('fr-FR')} · 💶 fond {euro(preview.session.openingFloatCents)}
              </div>
            </div>
          </div>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 180 }}>
              <div style={{ fontWeight: 800 }}>🔒 Caisse non ouverte</div>
              <div style={{ fontSize: '.8rem', color: 'var(--ink-faint)' }}>
                Ouvrez la caisse en début de service : votre nom sera inscrit sur le Ticket Z.
              </div>
            </div>
            <button className="btn-gold" style={{ width: 'auto', padding: '10px 18px', marginTop: 0 }} onClick={() => setAskOpen(true)}>🔓 Ouvrir le service</button>
          </div>
        )}

        {/* Identification obligatoire : le nom saisi ici est celui qui figurera sur le Ticket Z. */}
        {askOpen && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--line)' }}>
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <button className="cf-reprint" style={{ width: 'auto', padding: '7px 14px', fontWeight: mode === 'pin' ? 900 : 600, opacity: mode === 'pin' ? 1 : 0.55 }} onClick={() => setMode('pin')}>🔢 Code PIN</button>
              <button className="cf-reprint" style={{ width: 'auto', padding: '7px 14px', fontWeight: mode === 'admin' ? 900 : 600, opacity: mode === 'admin' ? 1 : 0.55 }} onClick={() => setMode('admin')}>👤 Nom + mot de passe</button>
            </div>
            {mode === 'pin' ? (
              <input className="inp" type="password" inputMode="numeric" placeholder="Votre code PIN" value={pin}
                onChange={(e) => setPin(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && doOpen()} style={{ maxWidth: 260 }} />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 260 }}>
                <input className="inp" placeholder="Votre nom" value={name} onChange={(e) => setName(e.target.value)} />
                <input className="inp" type="password" placeholder="Mot de passe" value={pwd}
                  onChange={(e) => setPwd(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && doOpen()} />
              </div>
            )}
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: '.85rem', fontWeight: 700, color: 'var(--ink-dim)', marginBottom: 4 }}>💶 Fond de caisse (monnaie de départ)</div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', maxWidth: 200 }}>
                <input className="inp" inputMode="decimal" placeholder="0,00" value={float} onChange={(e) => setFloat(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && doOpen()} />
                <span style={{ fontWeight: 800, color: 'var(--ink-dim)' }}>€</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button className="btn-gold" style={{ width: 'auto', padding: '10px 18px', marginTop: 0 }} disabled={opening} onClick={doOpen}>
                {opening ? '…' : '✓ Prendre le service'}
              </button>
              <button className="cf-reprint" style={{ width: 'auto', padding: '10px 14px' }} onClick={() => setAskOpen(false)}>Annuler</button>
            </div>
            <div style={{ fontSize: '.76rem', color: 'var(--ink-faint)', marginTop: 6 }}>
              Le nom et le fond de caisse seront imprimés sur le Ticket Z, avec le total attendu en espèces.
            </div>
          </div>
        )}
      </div>

      {/* Fin de service — provisoire tant que la caisse n'est pas clôturée */}
      <div className="cc" style={{ maxWidth: 620, marginBottom: 16 }}>
        <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '1.2rem', marginBottom: 6 }}>📅 Service en cours (provisoire)</div>
        {!preview ? <div className="no-data">Chargement…</div> : (
          <>
            {row('Commandes', String(preview.orderCount))}
            {row('Articles vendus', String(det?.itemsSold ?? preview.itemsSold))}
            {row('Panier moyen', euro(det?.avgBasketCents ?? preview.avgBasketCents))}
            {row('Total HT', euro(preview.htCents))}
            {row('TVA', euro(preview.taxCents))}
            {row('TOTAL TTC', euro(preview.revenueCents), true)}

            {section('PAR EMPLOYÉ', !det?.byStaff.length ? empty('Aucun encaissement.') : (
              <div>
                {det.byStaff.map((s) => (
                  <div key={s.name} style={{ marginBottom: 6 }}>
                    {row(`👤 ${s.name}`, `${euro(s.collectedCents)} · ${s.paymentCount} encaissement(s)`)}
                    {s.voidCount > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0 2px 14px', fontSize: '.85rem', color: '#c0392b' }}>
                        <span>{s.voidCount} annulation(s) de paiement</span><span>−{euro(s.voidedCents)}</span>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ))}

            {det && det.openingFloatCents !== undefined && section('ESPÈCES (TIROIR)', (
              <div>
                {row('Fond de caisse', euro(det.openingFloatCents))}
                {row('Espèces encaissées', euro(det.cashCollectedCents))}
                {row('Attendu en tiroir', euro(det.cashExpectedCents), true)}
              </div>
            ))}

            {section('ENCAISSEMENTS', Object.entries(preview.byMethod).length === 0
              ? empty('Aucun encaissement.')
              : Object.entries(preview.byMethod).map(([k, v]) => <div key={k}>{row(PAY_LABEL[k] ?? k, euro(v))}</div>))}

            {section('PAR TYPE DE COMMANDE', Object.entries(preview.byType).length === 0
              ? empty('Aucune commande.')
              : Object.entries(preview.byType).map(([k, v]) => <div key={k}>{row(TYPE_LABEL[k] ?? k, euro(v))}</div>))}

            {section('PRODUITS VENDUS', !det?.byCategory.length ? empty('Aucune vente.') : (
              <div>
                {det.byCategory.map((c) => (
                  <div key={c.category} style={{ marginBottom: 8 }}>
                    {row(`🗂️ ${c.category}`, `${c.qty} art. · ${euro(c.revenueCents)}`)}
                    {c.products.map((p) => (
                      <div key={p.name} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0 2px 14px', fontSize: '.85rem', color: 'var(--ink-dim)' }}>
                        <span>{p.qty}× {p.name}</span><span>{euro(p.revenueCents)}</span>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            ))}

            {section(`COMMANDES ANNULÉES (${det?.cancelled.length ?? 0})`, !det?.cancelled.length ? empty('Aucune.') : (
              <div>
                {det.cancelled.map((c) => (
                  <div key={c.number} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '.88rem' }}>
                    <span>{c.token ?? '#' + c.number} · {TYPE_LABEL[c.type] ?? c.type}{c.clientName ? ` · ${c.clientName}` : ''}{c.by ? ` · 👤 ${c.by}` : ''}</span>
                    <span style={{ color: '#c0392b' }}>{euro(c.totalCents)}</span>
                  </div>
                ))}
              </div>
            ))}

            {section(`PAIEMENTS ANNULÉS / MODIFIÉS (${det?.voids.length ?? 0})`, !det?.voids.length ? empty('Aucun.') : (
              <div>
                {det.voids.map((v, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '.88rem' }}>
                    <span>{v.orderToken ?? '#' + v.orderNumber} · {PAY_LABEL[v.method] ?? v.method}{v.by ? ` · 👤 ${v.by}` : ''}</span>
                    <span style={{ color: '#c0392b' }}>{euro(v.amountCents)}</span>
                  </div>
                ))}
              </div>
            ))}

            {section('STOCK RESTANT', !det?.stock.length ? empty('Aucun produit suivi en stock.') : (
              <div>
                {det.stock.map((s) => (
                  <div key={s.name} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '.88rem' }}>
                    <span>{s.name} <span style={{ color: 'var(--ink-faint)', fontSize: '.78rem' }}>· {s.category}</span></span>
                    <span style={{ fontWeight: 800, color: s.low ? '#c0392b' : 'var(--ink)' }}>{s.quantity}{s.low ? ' ⚠️ bas' : ''}</span>
                  </div>
                ))}
              </div>
            ))}

            {/* Tant qu'une commande est ouverte, la clôture est refusée : on dit lesquelles terminer. */}
            {blocked && (
              <div style={{ marginTop: 14, padding: 12, borderRadius: 10, background: '#fdecec', border: '1px solid #f3b9b9' }}>
                <div style={{ fontWeight: 900, color: '#c0392b', marginBottom: 6 }}>
                  ⛔ Clôture impossible : {preview.openOrders.length} commande(s) encore ouverte(s)
                </div>
                <div style={{ fontSize: '.85rem', color: '#7a2b23', marginBottom: 8 }}>
                  Terminez ou encaissez ces tables avant de clôturer la caisse.
                </div>
                {preview.openOrders.map((o) => (
                  <div key={o.number} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', fontSize: '.9rem' }}>
                    <span>
                      <b>{o.token ?? '#' + o.number}</b> · {TYPE_LABEL[o.type] ?? o.type}
                      {o.clientName ? ` · ${o.clientName}` : ''} · {STATUS_LABEL[o.status] ?? o.status}
                    </span>
                    <span style={{ fontWeight: 800, color: o.paid ? '#1f9150' : '#c0392b' }}>
                      {o.paid ? 'payée' : `à encaisser · ${euro(o.totalCents)}`}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <button
              className="btn-gold"
              style={{ marginTop: 16, width: 'auto', padding: '11px 22px', opacity: blocked ? 0.45 : 1 }}
              disabled={closing || blocked}
              onClick={doClose}
            >
              🔒 Clôturer la caisse (Ticket Z)
            </button>
            <div style={{ fontSize: '.76rem', color: 'var(--ink-faint)', marginTop: 6 }}>
              {blocked
                ? 'Le bouton se débloquera quand toutes les commandes seront terminées.'
                : 'Le Ticket Z part sur l’imprimante de la caisse, et les numéros de commande repartent à 101.'}
            </div>
          </>
        )}
      </div>

      {/* Historique des clôtures Z */}
      <div className="cc" style={{ maxWidth: 620 }}>
        <div style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '1.2rem', marginBottom: 6 }}>📜 Clôtures précédentes</div>
        {list.length === 0 && <div style={{ fontSize: '.85rem', color: 'var(--ink-faint)' }}>Aucune clôture pour l'instant.</div>}
        {list.map((z) => (
          <div key={z.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 0', borderBottom: '1px solid var(--line)', flexWrap: 'wrap' }}>
            <span style={{ fontFamily: "'Bebas Neue',sans-serif", fontSize: '1.1rem', color: 'var(--gold)', minWidth: 60 }}>Z n°{z.number}</span>
            <div style={{ flex: '1 1 140px', minWidth: 120 }}>
              <div style={{ fontWeight: 800 }}>{euro(z.revenueCents)} <span style={{ color: 'var(--ink-faint)', fontWeight: 700, fontSize: '.82rem' }}>· {z.orderCount} cmd</span></div>
              <div style={{ fontSize: '.76rem', color: 'var(--ink-faint)' }}>{new Date(z.createdAt).toLocaleString('fr-FR')}</div>
            </div>
            <button className="cf-reprint" style={{ width: 'auto', padding: '8px 12px' }} onClick={() => printZ(z)}>📄 Aperçu / PDF</button>
            <button className="cf-reprint" style={{ width: 'auto', padding: '8px 12px' }} onClick={() => printThermal(z)}>🖨️ Imprimer</button>
          </div>
        ))}
      </div>
    </div>
  );
}
